import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { usersApi } from '../../api/users.api';
import { ArrowLeftIcon, ClockIcon } from '@heroicons/react/24/outline';

export default function UserNavigationHistoryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await usersApi.getNavigationHistory(id);
        setData(response.data);
        // Select the most recent date by default
        if (response.data.dates?.length > 0) {
          setSelectedDate(response.data.dates[0]);
        }
      } catch (err) {
        setError('Failed to load navigation history');
        console.error('Error fetching navigation history:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [id]);

  const formatDuration = (ms) => {
    if (!ms || ms < 1000) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="mb-6">
          <Button variant="secondary" onClick={() => navigate(`/users/${id}`)}>
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to User
          </Button>
        </div>
        <div className="text-center py-12 text-gray-500">
          {error || 'No navigation history found'}
        </div>
      </DashboardLayout>
    );
  }

  const { user, dates, pageVisitsByDate } = data;
  const rawPageVisits = pageVisitsByDate[selectedDate] || [];

  // Remove duplicates with the same timestamp
  const currentPageVisits = rawPageVisits
    .filter((visit, index, self) =>
      index === self.findIndex(v => v.timestamp === visit.timestamp)
    );

  // Calculate total filtered page visits across all dates
  const filteredTotalPageVisits = dates.reduce((total, date) => {
    const visits = pageVisitsByDate[date] || [];
    const filtered = visits.filter((visit, index, self) =>
      index === self.findIndex(v => v.timestamp === visit.timestamp)
    );
    return total + filtered.length;
  }, 0);

  return (
    <DashboardLayout>
      <div className="mb-6">
        <Button variant="secondary" onClick={() => navigate(`/users/${id}`)}>
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to User
        </Button>
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {user.fullName}'s Navigation History
            </h1>
            <p className="text-gray-500">@{user.username}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Total Page Visits</p>
            <p className="text-3xl font-bold text-gray-900">{filteredTotalPageVisits}</p>
          </div>
        </div>
      </div>

      {/* Date Selector */}
      {dates.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Date</h2>
          <div className="flex flex-wrap gap-2">
            {dates.map((date) => (
              <button
                key={date}
                onClick={() => setSelectedDate(date)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedDate === date
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {new Date(date).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Page Visits Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Page Visits on {selectedDate ? new Date(selectedDate).toLocaleDateString() : 'Selected Date'}
            {currentPageVisits.length > 0 && (
              <span className="text-gray-500 font-normal ml-2">
                ({currentPageVisits.length} visits)
              </span>
            )}
          </h2>
        </div>

        {currentPageVisits.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No page visits recorded for this date.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Route
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentPageVisits.map((visit, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {formatTime(visit.timestamp)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="secondary" className="font-mono">
                        {visit.routePath === '/' ? 'Dashboard' : visit.routePath}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDuration(visit.durationMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
